//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// onboarding-common
import { IContainer, IProps, IState } from "./OnboardingSurvey.d";
import * as s from "./OnboardingSurvey.styles";
import {
  QuestionType,
  CURRENT_POSITION_RESPONSES,
  CURRENT_POSITION_QUESTION,
  TYPE_OF_WORK_RESPONSES,
  TYPE_OF_WORK_QUESTION,
  HEARD_ABOUT_CLOCKWISE_RESPONSES,
  HEARD_ABOUT_CLOCKWISE_QUESTION,
} from "./OnboardingSurvey.constant";
// other internals
import { logger } from "@util/logger.util";
// state
import { IReduxState } from "@reducers/root.reducer";
import { snackBarOpen } from "@actions/snack-bar.actions";
import { saveUserSurveyResponses } from "@mutations";
// material-ui
import { H1 } from "@ui";
import { Button, FormControl, InputLabel, MenuItem, Select } from "@material-ui/core";
import NavigateBefore from "@material-ui/icons/NavigateBefore";
import NavigateAfter from "@material-ui/icons/NavigateNext";

//////////////////
// COMPONENT
//////////////////
class OnboardingSurveyCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = this.getSurveyResponses(props.org.userSurveyResponseWrapperErrorable);
  }

  public componentDidMount() {
    const newState = this.getSurveyResponses(this.props.org.userSurveyResponseWrapperErrorable);
    this.setState(newState);
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private getDefaultSurveyResponses = () => {
    return {
      teamType: {
        questionType: QuestionType.WhatTypeOfWorkDoYouDo,
        questionText: TYPE_OF_WORK_QUESTION,
        responseType: null,
        responseText: null,
      },
      currentPosition: {
        questionType: QuestionType.WhatIsYourCurrentPosition,
        questionText: CURRENT_POSITION_QUESTION,
        responseType: null,
        responseText: null,
      },
      heardAboutClockwise: {
        questionType: QuestionType.HowDidYouHearAboutClockwise,
        questionText: HEARD_ABOUT_CLOCKWISE_QUESTION,
        responseType: null,
        responseText: null,
      },
    };
  };

  private getSurveyResponses = (
    userSurveyResponseWrapperErrorable: ISchema.UserSurveyResponseWrapperErrorable,
  ) => {
    const defaultResponses = this.getDefaultSurveyResponses();
    if (userSurveyResponseWrapperErrorable.__typename === "GraphEntityError") {
      logger.error("Failed to load user survey response");
      return defaultResponses;
    }

    const list = userSurveyResponseWrapperErrorable.userSurveyResponses;
    const teamType =
      list.find((usr) => usr.questionType === QuestionType.WhatTypeOfWorkDoYouDo) ||
      defaultResponses.teamType;
    const currentPosition =
      list.find((usr) => usr.questionType === QuestionType.WhatIsYourCurrentPosition) ||
      defaultResponses.currentPosition;
    const heardAboutClockwise =
      list.find((usr) => usr.questionType === QuestionType.HowDidYouHearAboutClockwise) ||
      defaultResponses.heardAboutClockwise;

    return { teamType, currentPosition, heardAboutClockwise };
  };

  private onSaveSuccess = () => {
    this.props.stepForward();
  };

  private onSaveError = () => {
    this.props.stepForward();
  };

  private saveResponses = () => {
    const { org, environment } = this.props;
    const { teamType, currentPosition, heardAboutClockwise } = this.state;

    const input = {
      userSurveyResponseWrapper: {
        id: org.id,
        userSurveyResponses: [
          teamType,
          currentPosition,
          heardAboutClockwise,
        ] as ISchema.IUserSurveyResponseInput[],
      } as ISchema.IUserSurveyResponseWrapper,
    };

    saveUserSurveyResponses(environment, input, this.onSaveSuccess, this.onSaveError);
  };

  // ~-~-~-~-~-~-~-
  // Handlers
  // ~-~-~-~-~-~-~-

  private onTeamTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const responseType = e.target.value;
    this.setState({
      teamType: {
        ...this.state.teamType,
        responseType,
        responseText: (TYPE_OF_WORK_RESPONSES.find((r) => r.responseType === responseType) as any)
          .responseText,
      },
    });
  };

  private onCurrentPositionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const responseType = e.target.value;
    this.setState({
      currentPosition: {
        ...this.state.currentPosition,
        responseType,
        responseText: (CURRENT_POSITION_RESPONSES.find(
          (r) => r.responseType === responseType,
        ) as any).responseText,
      },
    });
  };

  private onHeardAboutClockwiseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const responseType = e.target.value;
    this.setState({
      heardAboutClockwise: {
        ...this.state.heardAboutClockwise,
        responseType,
        responseText: (HEARD_ABOUT_CLOCKWISE_RESPONSES.find(
          (r) => r.responseType === responseType,
        ) as any).responseText,
      },
    });
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private renderButtons = () => {
    const { teamType, currentPosition, heardAboutClockwise } = this.state;
    const disabled =
      teamType.responseType === null ||
      currentPosition.responseType === null ||
      heardAboutClockwise.responseType === null;
    return (
      <div style={s.btnContainer}>
        <Button style={s.flatButton} variant="text" onClick={this.props.stepBack}>
          <NavigateBefore style={s.flatButtonIcon} />
          Back
        </Button>
        <Button
          variant="contained"
          style={s.primaryButton}
          color="primary"
          onClick={this.saveResponses}
          disabled={disabled}
        >
          Continue
          <NavigateAfter style={s.primaryButtonIcon} />
        </Button>
      </div>
    );
  };

  public render() {
    return (
      <React.Fragment>
        <div style={s.container}>
          <H1 style={s.H1}>First, help us get to know you.</H1>
          <div style={s.description}>
            Tell us a little about yourself and your team, so that we can better tailor Clockwise to
            your needs.
          </div>
          <div style={s.question.container}>
            <FormControl fullWidth>
              <InputLabel htmlFor="cw-type-of-work-select">{TYPE_OF_WORK_QUESTION}</InputLabel>
              <Select
                value={this.state.teamType.responseType}
                onChange={this.onTeamTypeChange}
                name={TYPE_OF_WORK_QUESTION}
                inputProps={{
                  name: { TYPE_OF_WORK_QUESTION },
                  id: "cw-type-of-work-select",
                }}
              >
                {TYPE_OF_WORK_RESPONSES.map((r, i) => (
                  <MenuItem key={i} value={r.responseType}>
                    {r.responseText}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div style={s.question.container}>
            <FormControl fullWidth>
              <InputLabel htmlFor="cw-current-position-select">
                {CURRENT_POSITION_QUESTION}
              </InputLabel>
              <Select
                value={this.state.currentPosition.responseType}
                onChange={this.onCurrentPositionChange}
                name={CURRENT_POSITION_QUESTION}
                inputProps={{
                  name: { CURRENT_POSITION_QUESTION },
                  id: "cw-current-position-select",
                }}
              >
                {CURRENT_POSITION_RESPONSES.map((r, i) => (
                  <MenuItem key={i} value={r.responseType}>
                    {r.responseText}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div style={s.question.container}>
            <FormControl fullWidth>
              <InputLabel htmlFor="cw-heard-about-select">
                {HEARD_ABOUT_CLOCKWISE_QUESTION}
              </InputLabel>
              <Select
                value={this.state.heardAboutClockwise.responseType}
                onChange={this.onHeardAboutClockwiseChange}
                name={HEARD_ABOUT_CLOCKWISE_QUESTION}
                inputProps={{
                  name: { HEARD_ABOUT_CLOCKWISE_QUESTION },
                  id: "cw-heard-about-select",
                }}
              >
                {HEARD_ABOUT_CLOCKWISE_RESPONSES.map((r, i) => (
                  <MenuItem key={i} value={r.responseType}>
                    {r.responseText}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>
        {this.renderButtons()}
      </React.Fragment>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const OnboardingSurveyRedux = connect(mapStateToProps, {
  snackBarOpen,
})(OnboardingSurveyCmpt);

//////////////////
// RELAY
//////////////////
import { fragments } from "./OnboardingSurvey.gql";
export const OnboardingSurvey = createFragmentContainer(OnboardingSurveyRedux, fragments);
